import { jsPDF } from 'jspdf';

export interface QuotationData {
  fullName: string;
  whatsappNumber: string;
  state: string;
  presetName: string;
  capacityKva: number;
  batteryCapacityKwh: number;
  solarPanelCapacityKw: number;
  priceNgn: number;
  dailyFuelSpend: number;
  monthlySavings: number;
  paybackMonths: number;
  date: string;
  quotationNo: string;
}

export const generateQuotationPdf = (data: QuotationData) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Corporate Header
  doc.setFillColor(15, 23, 42); // slate-900
  doc.rect(0, 0, pageWidth, 40, 'F');

  doc.setTextColor(16, 185, 129); // emerald-500
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('ACCESS TECH & RENEWABLE ENERGY', 14, 18);

  doc.setTextColor(203, 213, 225); // slate-300
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Umuahia Operational Headquarters: 14 Finidi Street, Umuahia, Abia State', 14, 25);
  doc.text('Email: info@accesstechnologiesandrenewableenergy.online | WhatsApp: +234 806 475 2074 | COREN Certified', 14, 31);

  // Document Title
  doc.setTextColor(15, 23, 42);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('FORMAL SOLAR SYSTEM PROPOSAL & QUOTATION', 14, 52);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Quotation No: ${data.quotationNo}`, pageWidth - 14, 52, { align: 'right' });
  doc.text(`Date: ${data.date}`, pageWidth - 14, 58, { align: 'right' });

  // Client Details Box
  doc.setFillColor(248, 250, 252);
  doc.rect(14, 65, pageWidth - 28, 26, 'F');
  doc.setLineWidth(0.5);
  doc.setDrawColor(226, 232, 240);
  doc.rect(14, 65, pageWidth - 28, 26, 'S');

  doc.setFont('helvetica', 'bold');
  doc.text('Prepared For:', 18, 73);
  doc.setFont('helvetica', 'normal');
  doc.text(`Client Name: ${data.fullName}`, 18, 80);
  doc.text(`Location: ${data.state}, Nigeria`, 18, 86);
  doc.text(`Phone/WhatsApp: ${data.whatsappNumber}`, pageWidth / 2 + 10, 80);

  // System Summary
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Selected System Package Summary', 14, 105);

  doc.setFillColor(240, 253, 244); // light emerald
  doc.rect(14, 110, pageWidth - 28, 24, 'F');
  
  doc.setFontSize(10);
  doc.text(`Package: ${data.presetName} (${data.capacityKva} kVA Pure Sine Hybrid)`, 18, 118);
  doc.text(`Storage: ${data.batteryCapacityKwh} kWh LiFePO4 Battery Bank`, 18, 128);
  doc.text(`Solar PV Array: ${data.solarPanelCapacityKw} kW Monocrystalline`, pageWidth / 2 + 10, 118);
  doc.text(`Total Package Price: ₦${data.priceNgn.toLocaleString()}`, pageWidth / 2 + 10, 128);

  // Bill of Materials (BOM) Table
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Itemized Bill of Materials (BOM)', 14, 148);

  const startY = 154;
  doc.setFillColor(15, 23, 42);
  doc.rect(14, startY, pageWidth - 28, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.text('Item Description', 18, startY + 5.5);
  doc.text('Qty / Rating', 120, startY + 5.5);
  doc.text('Status', pageWidth - 18, startY + 5.5, { align: 'right' });

  const bomItems = [
    { desc: `${data.capacityKva}kVA Hybrid Inverter (Pure Sine Wave, MPPT)`, qty: '1 Unit', status: 'Included' },
    { desc: `${data.batteryCapacityKwh}kWh Grade-A LiFePO4 Battery Bank`, qty: '1 Set', status: 'Included' },
    { desc: `${data.solarPanelCapacityKw}kW Monocrystalline Half-Cut Solar Panels`, qty: `${Math.round(data.solarPanelCapacityKw * 2)} Pcs`, status: 'Included' },
    { desc: 'Heavy-Duty Aluminum Mounting Rails & Clamps', qty: '1 Lot', status: 'Included' },
    { desc: 'DC/AC Surge Protection Devices & Changeover Box', qty: '1 Set', status: 'Included' },
    { desc: 'Flexible Trunking, 10mm/16mm DC Cables & Accessories', qty: '1 Lot', status: 'Included' },
    { desc: 'COREN Certified Engineering Installation & Commissioning', qty: 'Professional', status: 'Guaranteed' },
  ];

  let currentY = startY + 8;
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'normal');

  bomItems.forEach((item, index) => {
    if (index % 2 === 0) {
      doc.setFillColor(248, 250, 252);
      doc.rect(14, currentY, pageWidth - 28, 7, 'F');
    }
    doc.text(item.desc, 18, currentY + 4.5);
    doc.text(item.qty, 120, currentY + 4.5);
    doc.text(item.status, pageWidth - 18, currentY + 4.5, { align: 'right' });
    currentY += 7;
  });

  // Financial ROI Breakdown Box
  currentY += 8;
  doc.setFillColor(254, 252, 232); // amber light
  doc.rect(14, currentY, pageWidth - 28, 24, 'F');
  doc.setFont('helvetica', 'bold');
  doc.text('Financial Return on Investment (ROI) Projection', 18, currentY + 7);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Current Estimated Daily Fuel Spend: ₦${data.dailyFuelSpend.toLocaleString()}/day`, 18, currentY + 15);
  doc.text(`Estimated Monthly Fuel Savings: ₦${data.monthlySavings.toLocaleString()}/mo`, 18, currentY + 20);
  doc.text(`Payback Period: ~${data.paybackMonths} Months`, pageWidth / 2 + 10, currentY + 15);
  doc.text(`Warranty: 5-Year Lithium / 25-Year Panel`, pageWidth / 2 + 10, currentY + 20);

  // Footer & COREN Stamp
  currentY += 32;
  doc.setLineWidth(0.3);
  doc.setDrawColor(203, 213, 225);
  doc.line(14, currentY, pageWidth - 14, currentY);

  currentY += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('COREN CERTIFIED ENGINEERING STAMP & SIGNATURE:', 14, currentY);
  doc.text('AUTHORIZED SIGNATORY:', pageWidth / 2 + 10, currentY);

  currentY += 5;
  doc.setFont('helvetica', 'normal');
  doc.text('Engr. Chukwuma Access (MNSE, COREN Reg. R38291)', 14, currentY);
  doc.text('Head of Technical Operations, Access Tech Renewables', pageWidth / 2 + 10, currentY);

  currentY += 8;
  doc.setFontSize(7);
  doc.setTextColor(100, 116, 139);
  doc.text('This quotation is valid for 14 days from issue date due to exchange rate stability. Professional site survey required.', 14, currentY);

  // Save PDF
  doc.output('dataurlnewwindow');
};
